import type { ChangeRequest, LedgerEntry, Posting, PostingPhase, ScheduleBoardView, SwapRequest } from '../types.js'
import { describeDay, SHIFT_TYPE_LABELS } from './preferencesText.js'

export const PHASE_LABEL: Record<PostingPhase, string> = { waiting: 'Waiting for the open board', offers: 'Open board, offered in turn', first_come: 'Open, first to accept', locked: 'Last hours before the start', ended: 'Over, went unfilled' }
export const PHASE_CLASS: Record<PostingPhase, string> = { waiting: 'bg-canvas text-ink-mid border-marine/20', offers: 'bg-marine/10 text-marine border-marine/30', first_come: 'bg-coral-lt text-coral border-coral/50', locked: 'bg-ink/10 text-ink border-ink/30', ended: 'bg-canvas text-ink-mid border-marine/15' }
export const SOURCE_LABEL: Record<Posting['source'], string> = { unfilled: 'Unfilled by the run', vacated: 'Vacated', bridge_callout: 'Bridge callout, unanswered', census_rise: 'Census: a held seat released', demo: 'Demo seat' }
export const SWAP_STATUS_LABEL: Record<SwapRequest['status'], string> = { offered: 'Offered', needs_approval: 'Needs approval', completed: 'Completed', denied: 'Denied', cancelled: 'Cancelled', exhausted: 'Nobody could take it' }
export const SWAP_STATUS_CLASS: Record<SwapRequest['status'], string> = { offered: 'text-marine', needs_approval: 'text-coral', completed: 'text-green', denied: 'text-coral', cancelled: 'text-ink-mid', exhausted: 'text-ink-mid' }
export const CHANGE_STATUS_LABEL: Record<ChangeRequest['status'], string> = { pending: 'Pending the close', granted: 'Granted', declined: 'Declined' }

/** "Mar 3 day (07:00–15:00)". */
/** A unit's name by its id, from the facility's units; an id the facility does not list reads as written. */
export type UnitNames = ReadonlyMap<string, string>

export function unitNamesOf(units: readonly { id: string; name: string }[]): UnitNames {
  return new Map(units.map((unit) => [unit.id, unit.name]))
}

export function unitNameOf(unitId: string, units: UnitNames): string {
  return units.get(unitId) ?? unitId
}

/** A seat in words: the day, the shift type and hours, the unit by name and the role. */
export function describePostingShift(shift: Posting['shift'], units: UnitNames = new Map()): string {
  return `${describeDay(shift.date)} ${SHIFT_TYPE_LABELS[shift.shiftType].toLowerCase()} (${shift.startTime}–${shift.endTime}) · ${unitNameOf(shift.unitId, units)} · ${shift.role ?? 'any role'}`
}

/** A shift named by its id, in words, from the month as the board holds it; an id the month no longer holds reads as written. */
export function describeShiftById(shiftId: string, schedule: ScheduleBoardView['schedule'], units: UnitNames = new Map()): string {
  const assignment = schedule.flatMap((entry) => entry.assignments).find((entry) => entry.shiftId === shiftId)
  if (!assignment) {
    return shiftId
  }
  return `${describeDay(assignment.date)} ${SHIFT_TYPE_LABELS[assignment.shiftType].toLowerCase()} · ${unitNameOf(assignment.unitId, units)}${assignment.role ? ` · ${assignment.role}` : ''}`
}

export function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** The words of the reasons a swap was held for staff. */
const APPROVAL_REASON_TEXT: Record<string, string> = { cross_unit: 'off their home unit', different_tiers: 'different tiers' }

export function describeApprovalReasons(reasons: readonly string[]): string {
  return reasons.map((reason) => APPROVAL_REASON_TEXT[reason] ?? reason.replace(/_/g, ' ')).join(', ')
}

/** One line per ledger entry, for a history list; `names` turns ids into people. */
export function describeLedgerEntry(entry: LedgerEntry, names: ReadonlyMap<string, string>): string {
  const who = (id: string): string => names.get(id) ?? id
  switch (entry.kind) {
    case 'shift_type_changed':
      return `${who(entry.workerId)} changed shift type (${entry.fromShiftId} to ${entry.toShiftId}) at the window's close`
    case 'swapped':
      return `${who(entry.workerId)} ${entry.theirShiftId ? 'exchanged shifts with' : 'handed a shift to'} ${who(entry.withWorkerId)} (${entry.via === 'change_window' ? 'change window' : 'trade board'})`
    case 'swap_reversed':
      return `${who(entry.by)} undid a swap`
    case 'vacated':
      return `${who(entry.workerId)}'s ${entry.shiftId} was vacated: ${entry.reason}`
    case 'picked_up':
      return `${who(entry.workerId)} took a posting ${entry.mode === 'override' ? `by supervisor override (${entry.reasonCode}${describeOverridden(entry.overriddenRules)})` : entry.mode === 'first_come' ? 'first come' : 'from an offer'}`
    case 'flexed_off':
      return `${who(entry.workerId)}'s ${entry.shiftId} was released for low census (${entry.step === 'tier' ? 'lowest tier first' : entry.step === 'volunteers' ? 'volunteered' : entry.step}); the seat is held`
    case 'floated':
      return `${who(entry.workerId)} floated from ${entry.fromShiftId} to ${entry.toShiftId} as the census fell`
    case 'recalled':
      return `${who(entry.workerId)} took ${entry.shiftId} back on recall`
    case 'released_to_board':
      return `Held seat ${entry.shiftId} went to the open board`
    case 'census_seat_added':
      return entry.evaluationId === null ? 'Staff posted a seat the census required on the open board' : 'The census called for a seat the month did not hold: it was posted on the open board'
  }
}

const OVERRIDDEN_RULE_TEXT: Record<string, string> = { day_after_evening: 'a day shift after an evening', max_consecutive_days: 'too many days in a row' }

/** ", passing: a day shift after an evening" for an override that passed month rules; nothing otherwise. */
export function describeOverridden(rules: readonly string[] | undefined): string {
  return rules && rules.length > 0 ? `, passing: ${rules.map((rule) => OVERRIDDEN_RULE_TEXT[rule] ?? rule.replace(/_/g, ' ')).join(' and ')}` : ''
}
